import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import { Card, Button } from '../components/ui';
import { motion, useReducedMotion, type Variants } from 'motion/react';
import { INVITATION_TOKEN_KEY } from '../constants/storageKeys';
import { APP_HOME } from '../utils/segment';

const AcceptInvitePage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user, isAuthenticated, isLoading } = useAuthStore();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [message, setMessage] = useState('');
    const hasAttempted = useRef(false);
    const prefersReducedMotion = useReducedMotion();

    const token = searchParams.get('token');

    // Minimal entrance: fade-in only (no y translation — status resolves quickly)
    const panelVariants: Variants = {
        hidden: prefersReducedMotion ? { opacity: 1 } : { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] as const } },
    };

    // Safety net: never leave the user staring at an infinite "Processando
    // convite..." spinner. If the auth store never resolves (isLoading stuck)
    // or the accept request hangs, surface an actionable error after a timeout.
    useEffect(() => {
        if (status !== 'loading') return;
        const timer = setTimeout(() => {
            setStatus('error');
            setMessage('O convite está demorando mais que o esperado. Verifique sua conexão e tente novamente.');
        }, 12000);
        return () => clearTimeout(timer);
    }, [status]);

    useEffect(() => {
        if (hasAttempted.current) return;

        if (!token) {
            setStatus('error');
            setMessage('Token de convite não encontrado.');
            return;
        }

        // Fast path: if no auth token in localStorage, redirect immediately
        // Don't wait for auth store to finish loading
        const authToken = localStorage.getItem('auth_token');
        if (!authToken) {
            hasAttempted.current = true;
            localStorage.setItem(INVITATION_TOKEN_KEY, token);
            navigate(`/register?invite=${token}`, { replace: true });
            return;
        }

        // Auth token exists — wait for store to resolve
        if (isLoading) return;

        if (!isAuthenticated) {
            // Token existed but was invalid/expired
            hasAttempted.current = true;
            localStorage.setItem(INVITATION_TOKEN_KEY, token);
            navigate(`/register?invite=${token}`, { replace: true });
            return;
        }

        // User already belongs to a company
        if (user?.companyId) {
            hasAttempted.current = true;
            // Drop the stored token: it does not apply to this user right now,
            // and leaving it behind would get silently replayed on a later
            // login (e.g. after leaving the company), joining them unintentionally.
            localStorage.removeItem(INVITATION_TOKEN_KEY);
            setStatus('error');
            setMessage('Você já pertence a uma empresa. Para aceitar este convite, primeiro saia da empresa atual.');
            return;
        }

        hasAttempted.current = true;

        const acceptInvite = async () => {
            try {
                const response = await api.post('/company/invite/accept-token', { token });
                const data = response.data as any;
                if (data.success === false) {
                    // Rejected (expired / email mismatch): drop the token so it
                    // is not silently replayed on a later login.
                    localStorage.removeItem(INVITATION_TOKEN_KEY);
                    setStatus('error');
                    setMessage(data.message || 'Erro ao aceitar convite.');
                    toast.error(data.message || 'Erro ao aceitar convite.');
                    return;
                }
                setStatus('success');
                setMessage('Convite aceito com sucesso! Redirecionando...');
                toast.success('Convite aceito!');
                localStorage.removeItem(INVITATION_TOKEN_KEY);
                setTimeout(() => navigate('/organization'), 2000);
            } catch (error: any) {
                const msg = error.response?.data?.message || 'Erro ao aceitar convite. Token inválido ou expirado.';
                // A hard failure here (invalid/expired token) should not linger
                // in storage to be replayed later; the URL token still backs the
                // "Tentar novamente" reload for transient errors.
                localStorage.removeItem(INVITATION_TOKEN_KEY);
                setStatus('error');
                setMessage(msg);
                toast.error(msg);
            }
        };

        acceptInvite();
    }, [token, isAuthenticated, isLoading, user, navigate]);

    return (
        <div className="min-h-screen bg-snapfy-bg flex items-center justify-center">
            <motion.div
                className="max-w-md w-full"
                variants={panelVariants}
                initial="hidden"
                animate="visible"
            >
                <Card className="p-8 text-center">
                    {status === 'loading' && (
                        <>
                            <svg className="animate-spin h-8 w-8 text-snapfy-primary mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <h2 className="text-xl font-semibold text-snapfy-text-primary mb-2">Processando convite...</h2>
                            <p className="text-snapfy-text-secondary">Aguarde um momento.</p>
                        </>
                    )}
                    {status === 'success' && (
                        <>
                            <svg className="w-12 h-12 text-green-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h2 className="text-xl font-semibold text-snapfy-text-primary mb-2">Convite Aceito!</h2>
                            <p className="text-snapfy-text-secondary">{message}</p>
                        </>
                    )}
                    {status === 'error' && (
                        <>
                            <svg className="w-12 h-12 text-snapfy-error mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h2 className="text-xl font-semibold text-snapfy-text-primary mb-2">Erro no Convite</h2>
                            <p className="text-snapfy-text-secondary mb-4">{message}</p>
                            <div className="flex justify-center gap-3">
                                <Button
                                    variant="primary"
                                    onClick={() => window.location.reload()}
                                >
                                    Tentar novamente
                                </Button>
                                <Button
                                    variant="solid"
                                    onClick={() => navigate(APP_HOME)}
                                >
                                    Ir para Home
                                </Button>
                                {!isAuthenticated && (
                                    <Button
                                        variant="ghost"
                                        onClick={() => navigate('/login')}
                                    >
                                        Fazer Login
                                    </Button>
                                )}
                            </div>
                        </>
                    )}
                </Card>
            </motion.div>
        </div>
    );
};

export default AcceptInvitePage;
